/**
 * Мультиязычность без библиотек.
 *
 * Словари разбиты по областям: i18n/messages/<область>.ts экспортирует
 * defineMessages({ ru, en, be }). ru — эталон, en и be обязаны повторить
 * все его ключи с тем же типом (строка или функция от параметров), иначе
 * tsc упадёт. Ключи с префиксом области: 'settings.title'.
 *
 * Текущий язык — модульная переменная: её читают и компоненты (через t), и
 * чистые функции вроде lib/format.ts. LocaleProvider при смене языка
 * перемонтирует дерево (key), поэтому отдельная подписка не нужна.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { normalizeLanguage, type Language } from '@trestly/shared';
import { messages } from './messages';

export type Dict = typeof messages.ru;
export type DictKey = keyof Dict;

/** BCP 47 для Intl. */
export const INTL_LOCALE: Record<Language, string> = { en: 'en-US', ru: 'ru-RU', be: 'be-BY' };

/** Самоназвания языков: не переводятся. */
export const LANGUAGE_NAMES: Record<Language, string> = {
  en: 'English',
  ru: 'Русский',
  be: 'Беларуская',
};

let current: Language = detectInitialLanguage();

/** До ответа /api/settings: язык клиента Telegram (или браузера). */
function detectInitialLanguage(): Language {
  try {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const data = params.get('tgWebAppData') ?? sessionStorage.getItem('__telegram__initParams');
    if (data) {
      const raw = data.startsWith('{') ? (JSON.parse(data) as { tgWebAppData?: string }).tgWebAppData ?? '' : data;
      const user = new URLSearchParams(raw).get('user');
      if (user) return normalizeLanguage((JSON.parse(user) as { language_code?: string }).language_code);
    }
  } catch {
    /* вне Telegram */
  }
  return normalizeLanguage(navigator.language);
}

export function getLanguage(): Language {
  return current;
}

export function intlLocale(): string {
  return INTL_LOCALE[current];
}

type Params<K extends DictKey> = Dict[K] extends (p: infer P) => string ? [P] : [];

/** Перевод по ключу: t('settings.title'), t('due.inDays', { n: 3 }). */
export function t<K extends DictKey>(key: K, ...params: Params<K>): string {
  const value = (messages[current][key] ?? messages.ru[key]) as string | ((p: unknown) => string);
  return typeof value === 'function' ? value(params[0]) : value;
}

/**
 * Склонение по правилам текущего языка. forms — по категориям
 * Intl.PluralRules (one/few/many/other); недостающую берём из other.
 * В ru и be нужны one/few/many, в en: one/other.
 */
export function plural(count: number, forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }): string {
  const rule = new Intl.PluralRules(intlLocale()).select(count);
  return forms[rule] ?? forms.other;
}

const LocaleContext = createContext<Language>(current);

export function useLanguage(): Language {
  return useContext(LocaleContext);
}

/** Сразу переключить язык, до ответа сервера (оптимистично). */
export function setLanguageNow(language: Language): void {
  current = language;
}

/** Применяет язык из настроек и перерисовывает дерево при смене. */
export function LocaleProvider({ language, children }: { language: Language | undefined; children: ReactNode }): JSX.Element {
  const [lang, setLang] = useState<Language>(current);
  useEffect(() => {
    if (language) {
      current = language;
      setLang(language);
    }
  }, [language]);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return (
    <LocaleContext.Provider value={lang}>
      <div key={lang} className="contents">
        {children}
      </div>
    </LocaleContext.Provider>
  );
}
